import { c as _c } from "react/compiler-runtime";
import type { HofDashboardDeadline } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { StatusPill } from '@/features/hof-dashboard/components/dashboard-ui';
import { formatDate, formatTimeLeft, translate, translateCount } from '@/features/hof-dashboard/texts';
import { daysUntil } from '@/features/hof-dashboard/utils/submission-progress';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type React from 'react';

/** How many forms are due on a day, and how many of them still ask something of the Hof. */
export interface DueOnDay {
  forms: number;
  open: number;
}

/** What is due per day, as `daysUntil` counts it, from where each form stands. */
export const dueByDay = (forms: readonly {
  deadline: string | undefined;
  open: boolean;
}[], now: Date): Map<number, DueOnDay> => {
  const due = new Map<number, DueOnDay>();
  for (const form of forms) {
    if (form.deadline === undefined) continue;
    const day = daysUntil(form.deadline, now);
    const counted = due.get(day) ?? {
      forms: 0,
      open: 0
    };
    due.set(day, {
      forms: counted.forms + 1,
      open: counted.open + (form.open ? 1 : 0)
    });
  }
  return due;
};

/** One day on a timeline: its deadlines, or only today's marker. */
interface TimelineDay {
  date: string;
  daysLeft: number;
  titles: string[];
}

/** The deadlines by calendar day, soonest first. */
const byDay = (deadlines: readonly Pick<HofDashboardDeadline, 'date' | 'title'>[], now: Date): TimelineDay[] => {
  const days = new Map<number, TimelineDay>();
  for (const deadline of deadlines) {
    const daysLeft = daysUntil(deadline.date, now);
    const day = days.get(daysLeft) ?? {
      date: deadline.date,
      daysLeft,
      titles: []
    };
    day.titles.push(deadline.title);
    days.set(daysLeft, day);
  }
  return [...days.values()].toSorted((a, b) => a.daysLeft - b.daysLeft);
};
type NodeKind = 'current' | 'future' | 'passed';

/** Passed, the one that stands out, or still to come. */
const nodeKind = (day: TimelineDay, current: TimelineDay | undefined): NodeKind => {
  if (day === current) return 'current';
  return day.daysLeft < 0 ? 'passed' : 'future';
};

/** The dot of a day: today or the next one filled, a day to come hollow, one passed small and grey. */
const Node: React.FC<{
  kind: NodeKind;
}> = t0 => {
  const $ = _c(6);
  const {
    kind
  } = t0;
  const t1 = kind === "current" && "bg-conveniat-green ring-tint-line h-3.5 w-3.5 ring-4";
  const t2 = kind === "future" && "border-rail-node h-3.5 w-3.5 border-2 bg-white";
  const t3 = kind === "passed" && "h-2.5 w-2.5 bg-gray-300";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("rounded-full", t1, t2, t3);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <span aria-hidden={true} className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center"><span className={t4} /></span>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};

/** What is due on a day: how much is still open, or that all of it is done. */
const DueLine: React.FC<{
  due: DueOnDay | undefined;
  locale: Locale;
}> = t0 => {
  const $ = _c(10);
  const {
    due,
    locale
  } = t0;
  if (due === undefined || due.forms === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (due.open > 0) {
    let t1;
    if ($[1] !== due.open || $[2] !== locale) {
      t1 = translateCount(due.open, "openCountOne", "openCountMany", locale);
      $[1] = due.open;
      $[2] = locale;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1) {
      t2 = <p className="text-sm text-green-600">{t1}</p>;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== locale) {
    t1 = translate("doneGroup", locale);
    $[6] = locale;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <p className="pt-1"><StatusPill tone="done">{t1}</StatusPill></p>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};

/**
 * The camp's deadlines as a timeline down the page, with today marked where it falls. What
 * has passed is left out unless something due then is still open, so the first line is what
 * matters now. `due` says per day, as `daysUntil` counts it, what is due then.
 */
export const DeadlineTimeline: React.FC<{
  deadlines: readonly Pick<HofDashboardDeadline, 'date' | 'title'>[];
  due?: ReadonlyMap<number, DueOnDay>;
  locale: Locale;
}> = t0 => {
  const $ = _c(8);
  const {
    deadlines,
    due,
    locale
  } = t0;
  let t1;
  if ($[0] !== deadlines || $[1] !== due || $[2] !== locale) {
    const now = new Date();
    let t2;
    if ($[4] !== due) {
      t2 = day => day.daysLeft >= 0 || (due?.get(day.daysLeft)?.open ?? 0) > 0;
      $[4] = due;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const days = byDay(deadlines, now).filter(t2);
    if (!days.some(_temp)) {
      const today = {
        date: now.toISOString(),
        daysLeft: 0,
        titles: []
      };
      const at = days.findIndex(_temp2);
      days.splice(at === -1 ? days.length : at, 0, today);
    }
    t1 = days.map((day_2, index) => {
      const isToday = day_2.daysLeft === 0;
      const passed = day_2.daysLeft < 0;
      return <li key={day_2.daysLeft} className="relative flex gap-3 pb-6 last:pb-0">{index < days.length - 1 && <span aria-hidden={true} className="bg-rail absolute top-5 bottom-0 left-[9.5px] w-px" />}<Node kind={nodeKind(day_2, isToday ? day_2 : undefined)} /><div className="min-w-0 flex-1 space-y-0.5" aria-current={isToday ? "date" : undefined}><p className={cn("text-sm", passed ? "text-danger-text" : "text-green-700")}><span className="text-heading font-semibold tabular-nums">{isToday ? translate("todayOn", locale, {
                date: formatDate(day_2.date, locale)
              }) : formatDate(day_2.date, locale)}</span>{!isToday && ` · ${formatTimeLeft(day_2.daysLeft, locale)}`}</p>{day_2.titles.map(_temp3)}<DueLine due={due?.get(day_2.daysLeft)} locale={locale} /></div></li>;
    });
    $[0] = deadlines;
    $[1] = due;
    $[2] = locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <ol>{t1}</ol>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

/**
 * An area's deadlines across the page, the next one filled: what comes when, at a glance over
 * the area's forms. Scrolls sideways when there are more than fit.
 */
export const DeadlineStrip: React.FC<{
  deadlines: readonly Pick<HofDashboardDeadline, 'date' | 'title'>[];
  due?: ReadonlyMap<number, DueOnDay>;
  locale: Locale;
}> = t0 => {
  const $ = _c(13);
  const {
    deadlines,
    due,
    locale
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== deadlines || $[1] !== due || $[2] !== locale) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const days = byDay(deadlines, new Date());
      if (days.length === 0) {
        let t4;
        if ($[6] !== locale) {
          t4 = translate("noDeadlines", locale);
          $[6] = locale;
          $[7] = t4;
        } else {
          t4 = $[7];
        }
        let t5;
        if ($[8] !== t4) {
          t5 = <p className="text-sm text-green-600">{t4}</p>;
          $[8] = t4;
          $[9] = t5;
        } else {
          t5 = $[9];
        }
        t3 = t5;
        break bb0;
      }
      const next = days.find(_temp4);
      t1 = "grid auto-cols-[minmax(8rem,1fr)] grid-flow-col overflow-x-auto pb-1";
      t2 = days.map((day_0, index) => {
        const passed = day_0.daysLeft < 0;
        return <li key={day_0.daysLeft} className="min-w-0 space-y-1 pr-5"><div className="flex items-center gap-2"><Node kind={nodeKind(day_0, next)} />{index < days.length - 1 && <span aria-hidden={true} className={cn("h-px flex-1", passed ? "bg-gray-300" : "bg-rail")} />}</div><p className={cn("text-sm font-semibold", passed ? "text-gray-500" : "text-green-700")}><span className="tabular-nums">{formatDate(day_0.date, locale)}</span> ·{" "}{formatTimeLeft(day_0.daysLeft, locale)}</p>{day_0.titles.map(title => <p key={title} className={cn("text-sm font-semibold text-balance", passed ? "text-gray-500" : "text-heading")}>{title}</p>)}<DueLine due={due?.get(day_0.daysLeft)} locale={locale} /></li>;
      });
    }
    $[0] = deadlines;
    $[1] = due;
    $[2] = locale;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t2) {
    t4 = <ol className={t1}>{t2}</ol>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
function _temp(day_1) {
  return day_1.daysLeft === 0;
}
function _temp2(day_0) {
  return day_0.daysLeft > 0;
}
function _temp3(title) {
  return <p key={title} className="text-base text-balance text-green-900">{title}</p>;
}
function _temp4(day) {
  return day.daysLeft >= 0;
}